import { getIndicatorDefinition } from "../indicators/indicator-definitions.js";
import {
  MAX_INDICATORS,
  MAX_INDICATOR_PANES,
  createIndicatorInstance,
  normalizeIndicatorOrder,
  validateInput
} from "../indicators/indicator-model.js";

export const TEMPLATE_SCHEMA_VERSION = 1;
export const TEMPLATE_MAX_BYTES = 256 * 1024;
export const TEMPLATE_MAX_NAME_LENGTH = 120;
export const TEMPLATE_MAX_DESCRIPTION_LENGTH = 500;

const STYLE_FIELDS = new Set([
  "visible",
  "color",
  "width",
  "lineStyle",
  "opacity",
  "plotType",
  "priceLabel",
  "lastValueLabel"
]);

export class TemplateContractError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "TemplateContractError";
    this.code = code;
  }
}

export function normalizeTemplateName(value) {
  return String(value || "")
    .normalize("NFKC")
    .trim()
    .replace(/\s+/gu, " ")
    .toLocaleUpperCase("en-US");
}

export function validateTemplateMetadata(name, description = "") {
  const cleanName = String(name || "").normalize("NFKC").trim().replace(/\s+/gu, " ");
  const cleanDescription = String(description || "").normalize("NFKC").trim();
  if (!cleanName || cleanName.length > TEMPLATE_MAX_NAME_LENGTH) {
    throw invalid(`Template name must contain 1 to ${TEMPLATE_MAX_NAME_LENGTH} characters.`);
  }
  if (/[\u0000-\u001f\u007f<>\\/]/u.test(cleanName)) {
    throw invalid("Template name contains unsupported characters.");
  }
  if (cleanDescription.length > TEMPLATE_MAX_DESCRIPTION_LENGTH || cleanDescription.includes("\0")) {
    throw invalid(
      `Template description may contain at most ${TEMPLATE_MAX_DESCRIPTION_LENGTH} characters.`
    );
  }
  return {
    name: cleanName,
    normalizedName: normalizeTemplateName(cleanName),
    description: cleanDescription || null
  };
}

export function createIndicatorTemplatePayload(instances, paneHeights = {}) {
  const ordered = normalizeIndicatorOrder(
    (instances || []).map((instance) => ({
      ...instance,
      inputs: structuredClone(instance.inputs || {}),
      styles: structuredClone(instance.styles || {})
    }))
  );
  const paneOrder = ["main"];
  for (const instance of ordered) {
    if (!paneOrder.includes(instance.paneId)) paneOrder.push(instance.paneId);
  }
  const payload = {
    schemaVersion: TEMPLATE_SCHEMA_VERSION,
    indicators: ordered.map((instance) => ({
      definitionId: instance.definitionId,
      definitionVersion: String(
        getIndicatorDefinition(instance.definitionId)?.version || "1"
      ),
      paneId: instance.paneId,
      order: instance.order,
      scale: String(instance.scale || "right"),
      visible: instance.visible !== false,
      inputs: structuredClone(instance.inputs || {}),
      styles: structuredClone(instance.styles || {})
    })),
    paneOrder,
    paneHeights: Object.fromEntries(
      Object.entries(paneHeights || {})
        .filter(([paneId, height]) =>
          paneOrder.includes(paneId)
          && Number.isFinite(height)
          && height >= 1
          && height <= 10_000)
    )
  };
  assertPayloadSize(payload);
  return payload;
}

export function prepareIndicatorTemplate(payload) {
  const migrated = migrateTemplatePayload("indicator", payload);
  if (!Array.isArray(migrated.indicators) || migrated.indicators.length > MAX_INDICATORS) {
    throw invalid(`Indicator templates may contain at most ${MAX_INDICATORS} indicators.`);
  }
  if (
    !Array.isArray(migrated.paneOrder)
    || migrated.paneOrder.length < 1
    || migrated.paneOrder.length > MAX_INDICATOR_PANES
    || migrated.paneOrder[0] !== "main"
    || new Set(migrated.paneOrder).size !== migrated.paneOrder.length
    || migrated.paneOrder.some((paneId) => !safeIdentifier(paneId, 128))
  ) {
    throw invalid("Indicator pane order is invalid.");
  }
  const paneSet = new Set(migrated.paneOrder);
  const orderByPane = new Map();
  const instances = migrated.indicators.map((item) => {
    const definition = getIndicatorDefinition(item?.definitionId);
    if (!definition) {
      throw new TemplateContractError(
        "MissingIndicatorDefinition",
        `Indicator definition "${item?.definitionId || "unknown"}" is unavailable.`
      );
    }
    if (
      String(item.definitionVersion || "") !== String(definition.version || "1")
      || !paneSet.has(item.paneId)
      || !safeIdentifier(item.scale, 32)
      || !Number.isInteger(item.order)
      || item.order < 0
      || !plainObject(item.inputs)
      || !plainObject(item.styles)
    ) {
      throw invalid(`The saved ${definition.name} configuration is invalid.`);
    }
    const expectedOrder = orderByPane.get(item.paneId) || 0;
    if (item.order !== expectedOrder) {
      throw invalid("Indicator order must be contiguous within each pane.");
    }
    orderByPane.set(item.paneId, expectedOrder + 1);
    const inputKeys = new Set(definition.inputs.map((input) => input.key));
    if (Object.keys(item.inputs).some((key) => !inputKeys.has(key))) {
      throw invalid(`${definition.name} contains an obsolete input.`);
    }
    for (const input of definition.inputs) {
      if (!Object.hasOwn(item.inputs, input.key)) {
        throw invalid(`${definition.name} is missing the ${input.label} input.`);
      }
      const result = validateInput(definition, input.key, item.inputs[input.key], item.inputs);
      if (!result.valid) throw invalid(`${definition.name}: ${result.message}`);
    }
    validateStyles(definition, item.styles);
    return createIndicatorInstance(definition, {
      paneId: item.paneId,
      scale: item.scale,
      visible: item.visible !== false,
      inputs: structuredClone(item.inputs),
      styles: structuredClone(item.styles)
    });
  });
  const paneHeights = {};
  if (migrated.paneHeights !== undefined && !plainObject(migrated.paneHeights)) {
    throw invalid("Indicator pane heights are invalid.");
  }
  for (const [paneId, height] of Object.entries(migrated.paneHeights || {})) {
    if (!paneSet.has(paneId) || !Number.isFinite(height) || height < 1 || height > 10_000) {
      throw invalid("Indicator pane heights are invalid.");
    }
    paneHeights[paneId] = height;
  }
  assertPayloadSize(migrated);
  return {
    schemaVersion: TEMPLATE_SCHEMA_VERSION,
    instances: normalizeIndicatorOrder(instances),
    paneHeights
  };
}

export function createChartTemplatePayload(settings, settingDefinitions) {
  const values = {};
  for (const name of Object.keys(settingDefinitions)) {
    values[name] = structuredClone(settings[name]);
  }
  const payload = {
    schemaVersion: TEMPLATE_SCHEMA_VERSION,
    settings: values
  };
  assertPayloadSize(payload);
  return payload;
}

export function prepareChartTemplate(payload, settingDefinitions, defaults) {
  const migrated = migrateTemplatePayload("chart", payload);
  if (!plainObject(migrated.settings)) throw invalid("Chart template settings are invalid.");
  const definitions = Object.entries(settingDefinitions);
  if (!definitions.length || Object.keys(migrated.settings).length > definitions.length) {
    throw invalid("Chart template contains unsupported settings.");
  }
  const settings = { ...defaults };
  for (const [name, value] of Object.entries(migrated.settings)) {
    const definition = settingDefinitions[name];
    if (!definition) throw invalid(`Chart setting "${name}" is no longer supported.`);
    settings[name] = validateChartSetting(name, value, definition);
  }
  assertPayloadSize(migrated);
  return {
    schemaVersion: TEMPLATE_SCHEMA_VERSION,
    settings
  };
}

export function migrateTemplatePayload(type, payload) {
  if (!plainObject(payload)) throw invalid("Template payload must be an object.");
  const version = Number(payload.schemaVersion);
  if (version > TEMPLATE_SCHEMA_VERSION) {
    throw new TemplateContractError(
      "UnsupportedSchemaVersion",
      `This ${type} template uses newer schema version ${version}.`
    );
  }
  if (version !== TEMPLATE_SCHEMA_VERSION) {
    throw new TemplateContractError(
      "UnsupportedSchemaVersion",
      `This ${type} template uses unsupported schema version ${version || "unknown"}.`
    );
  }
  return structuredClone(payload);
}

export function materiallyEqual(left, right) {
  return stableJson(left) === stableJson(right);
}

function validateChartSetting(name, value, definition) {
  if (definition.type === "boolean") {
    if (typeof value !== "boolean") throw invalid(`${name} must be true or false.`);
    return value;
  }
  if (definition.type === "number") {
    if (
      typeof value !== "number"
      || !Number.isFinite(value)
      || value < definition.minimum
      || value > definition.maximum
    ) {
      throw invalid(`${name} is outside its supported range.`);
    }
    return value;
  }
  if (typeof value !== "string" || value.length > 256) {
    throw invalid(`${name} must be a supported text value.`);
  }
  if (definition.type === "color" && !/^#[0-9a-f]{6}$/iu.test(value)) {
    throw invalid(`${name} must be a six-digit color.`);
  }
  if (definition.options?.length && !definition.options.includes(value)) {
    throw invalid(`${name} is no longer a supported option.`);
  }
  return value;
}

function validateStyles(definition, styles) {
  const outputs = new Map(definition.outputs.map((output) => [output.key, output]));
  if (Object.keys(styles).some((key) => !outputs.has(key))) {
    throw invalid(`${definition.name} contains an obsolete style.`);
  }
  for (const output of definition.outputs) {
    const style = styles[output.key];
    if (!plainObject(style) || Object.keys(style).some((key) => !STYLE_FIELDS.has(key))) {
      throw invalid(`${definition.name} contains an invalid ${output.label} style.`);
    }
    for (const [key, value] of Object.entries(style)) {
      if (["visible", "priceLabel", "lastValueLabel"].includes(key) && typeof value !== "boolean") {
        throw invalid(`${definition.name} contains an invalid ${output.label} style.`);
      }
      if (key === "color" && !/^#[0-9a-f]{6}$/iu.test(String(value))) {
        throw invalid(`${definition.name} contains an invalid ${output.label} color.`);
      }
      if (key === "width" && (!Number.isFinite(value) || value < 0.5 || value > 10)) {
        throw invalid(`${definition.name} contains an invalid ${output.label} width.`);
      }
      if (key === "opacity" && (!Number.isFinite(value) || value < 0 || value > 100)) {
        throw invalid(`${definition.name} contains an invalid ${output.label} opacity.`);
      }
      if (key === "lineStyle" && !["solid", "dashed", "dotted"].includes(value)) {
        throw invalid(`${definition.name} contains an invalid ${output.label} line style.`);
      }
      if (key === "plotType" && !["line", "histogram", "fill"].includes(value)) {
        throw invalid(`${definition.name} contains an invalid ${output.label} plot type.`);
      }
    }
  }
}

function assertPayloadSize(payload) {
  const size = new TextEncoder().encode(JSON.stringify(payload)).length;
  if (size > TEMPLATE_MAX_BYTES) throw invalid("Template payload exceeds 256 KiB.");
}

function stableJson(value) {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (plainObject(value)) {
    return `{${Object.keys(value).sort().map(
      (key) => `${JSON.stringify(key)}:${stableJson(value[key])}`
    ).join(",")}}`;
  }
  return JSON.stringify(value);
}

function safeIdentifier(value, maximumLength) {
  return typeof value === "string"
    && value.length > 0
    && value.length <= maximumLength
    && /^[a-z0-9_.-]+$/iu.test(value);
}

function plainObject(value) {
  return value !== null
    && typeof value === "object"
    && !Array.isArray(value)
    && Object.getPrototypeOf(value) === Object.prototype;
}

function invalid(message) {
  return new TemplateContractError("InvalidPayload", message);
}
